import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from 'components/sales/layout/layout';
import { AppliedFiltersBar, TableSectionV2 } from 'components/UI';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Images from 'assets/images/imageData';
import FileUpload from 'components/sales/fileUpload/fileUpload';
import { downloadFastagReKyvSampleZip, downloadFastagVrnDetailSampleCsv, downloadFastagChassisToVrnSampleCsv } from 'assets/fastag/rekyv/sampleData';
import ModalPop from 'components/sales/modal/modal';
import {
  EMPTY_REKYV_LISTING_FILTERS,
  FASTAG_BULK_REQUEST_TYPES,
  fetchFastagBulkUploadListing,
  formatFileSize,
  LISTING_PAGE_SIZE,
  submitFastagBulkUpload,
  uploadFastagZipFile,
  validateFastagBulkFile,
  validateFastagBulkUploadRequest,
  MAX_ZIP_FILE_BYTES,
} from 'services/utils/fileUpload/presignedZipUpload';
import FastagReKyvListingFilter from './FastagReKyvListingFilter';
import './FastagReKyV.css';

const PHASE_LABELS = {
  creating: 'Preparing upload…',
  uploading: 'Uploading file…',
  completing: 'Finalizing upload…',
  validating: 'Validating CSV…',
  submitting: 'Submitting to FASTag…',
  done: 'Submission complete',
};

const STATUS_CLASS_MAP = {
  INITIATED: 'fastag-rekyv-status-initiated',
  PROCESSING: 'fastag-rekyv-status-processing',
  PROCESSED: 'fastag-rekyv-status-processed',
  FAIL: 'fastag-rekyv-status-fail',
};

const cell = (val) => (val != null && val !== '' ? String(val) : '—');

const isDownloadLinkColumn = (key, label) => {
  const normalizedKey = String(key || '').toLowerCase();
  const normalizedLabel = String(label || '').toLowerCase();

  return (
    normalizedKey === 'download_link' ||
    normalizedKey === 'file_link' ||
    normalizedKey.endsWith('_download_link') ||
    normalizedLabel.includes('download link') ||
    normalizedLabel === 'download'
  );
};

const DownloadIconLink = ({ href }) => {
  if (!href) return '—';

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="fastag-rekyv-download-icon"
      title="Download file"
      aria-label="Download file"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
      </svg>
    </a>
  );
};

const formatDownloadLink = (val) => {
  if (val == null || val === '') return '—';
  const url = String(val).trim();
  if (!url) return '—';
  return <DownloadIconLink href={url} />;
};

const formatApiColumnLabel = (label) =>
  String(label || '')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const buildTableColumns = (apiColumns = {}) => {
  const primaryKeys = Object.entries(apiColumns)
    .filter(([, config]) => Number(config?.is_primary) === 1)
    .map(([key]) => key);

  const keys = primaryKeys.length
    ? primaryKeys
    : [
        'request_id',
        'created_at',
        'request_type',
        'request_status',
        'requestor_name',
        'total_cases',
        'initiated_cases',
        'processing_cases',
        'success_cases',
        'failed_cases',
      ];

  const columns = keys.map((key) => {
    const label = formatApiColumnLabel(apiColumns[key]?.label || key.replace(/_/g, ' '));

    if (key === 'request_status') {
      return {
        key,
        label,
        format: (val) => (
          <span
            className={`fastag-rekyv-status ${
              STATUS_CLASS_MAP[val] || 'fastag-rekyv-status-default'
            }`}
          >
            {cell(val)}
          </span>
        ),
      };
    }

    if (isDownloadLinkColumn(key, apiColumns[key]?.label)) {
      return {
        key,
        label,
        format: (val) => formatDownloadLink(val),
      };
    }

    return {
      key,
      label,
      format: (val) => cell(val),
    };
  });

  columns.push({
    key: 'actions',
    label: 'Input',
    format: (_, row) => <DownloadIconLink href={row.file_link} />,
  });

  return columns;
};

const REQUEST_TYPE_OPTIONS = [
  { value: FASTAG_BULK_REQUEST_TYPES.KYV, label: 'KYV' },
  { value: FASTAG_BULK_REQUEST_TYPES.VRN_DETAIL, label: 'VRN_DETAIL' },
  { value: FASTAG_BULK_REQUEST_TYPES.CHASSIS_TO_VRN, label: 'CHASSIS_TO_VRN' },
  { value: FASTAG_BULK_REQUEST_TYPES.RC_UPLOAD, label: 'RC_UPLOAD' },
];

const UPLOAD_CONFIG = {
  [FASTAG_BULK_REQUEST_TYPES.KYV]: {
    title: 'Upload Re-KYV Zip File',
    subtitle: 'Upload a single zip archive for FASTag Re-KYV processing.',
    dropzoneIcon: '📦',
    dropzoneLabel: 'Drop your .zip file here',
    accept: '.zip,application/zip,application/x-zip-compressed',
    formatLabel: '.zip',
    maxSizeLabel: '250 MB',
    contentTypeLabel: 'application/zip',
    fileIcon: '🗜️',
    uploadButtonLabel: 'Upload Zip',
    successTitle: 'Re-KYV submitted successfully',
    successSubtitle: 'Your zip file was uploaded and submitted to FASTag for processing.',
    toastSuccess: 'Re-KYV file submitted successfully.',
    maxBytes: MAX_ZIP_FILE_BYTES,
    downloadSample: downloadFastagReKyvSampleZip,
  },
  [FASTAG_BULK_REQUEST_TYPES.VRN_DETAIL]: {
    title: 'Upload VRN Detail CSV File',
    subtitle: 'Upload a CSV file and submit it for Fastag Compliance (VRN Detail) processing.',
    formatLabel: '.csv',
    maxSizeLabel: '50 MB',
    uploadButtonLabel: 'Submit CSV',
    successTitle: 'VRN Detail submitted successfully',
    successSubtitle: 'Your CSV file was uploaded and submitted for Fastag Compliance processing.',
    toastSuccess: 'VRN Detail file submitted successfully.',
    downloadSample: downloadFastagVrnDetailSampleCsv,
  },
  [FASTAG_BULK_REQUEST_TYPES.CHASSIS_TO_VRN]: {
    title: 'Upload Chassis to VRN CSV File',
    subtitle: 'Upload a CSV file and submit it for Fastag Compliance (Chassis to VRN) processing.',
    formatLabel: '.csv',
    maxSizeLabel: '50 MB',
    uploadButtonLabel: 'Submit CSV',
    successTitle: 'Chassis to VRN submitted successfully',
    successSubtitle: 'Your CSV file was uploaded and submitted for Fastag Compliance processing.',
    downloadSample: downloadFastagChassisToVrnSampleCsv,
  },
  [FASTAG_BULK_REQUEST_TYPES.RC_UPLOAD]: {
    title: 'Upload RC CSV File',
    subtitle: 'Upload a CSV file and submit it for Fastag Compliance (RC Upload) processing.',
    formatLabel: '.csv',
    maxSizeLabel: '50 MB',
    uploadButtonLabel: 'Submit CSV',
    successTitle: 'RC Upload submitted successfully',
    successSubtitle: 'Your CSV file was uploaded and submitted for Fastag Compliance processing.',
    toastSuccess: 'RC Upload file submitted successfully.',
    downloadSample: downloadFastagVrnDetailSampleCsv,
  },
};

const FastagReKyV = ({
  documentUploadUrlPublic,
  documentUrlDataPublic,
}) => {
  const fileInputRef = useRef(null);
  const requestTypeRef = useRef(FASTAG_BULK_REQUEST_TYPES.KYV);
  const [requestType, setRequestType] = useState(FASTAG_BULK_REQUEST_TYPES.KYV);
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvFileLink, setCsvFileLink] = useState('');
  const [fileUploadResetKey, setFileUploadResetKey] = useState(0);
  const [fileError, setFileError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [listData, setListData] = useState([]);
  const [listingColumns, setListingColumns] = useState({});
  const [listingPageCount, setListingPageCount] = useState(0);
  const [listingCurrentPage, setListingCurrentPage] = useState(1);
  const [listingTotalCount, setListingTotalCount] = useState(0);
  const [listingLimit, setListingLimit] = useState(LISTING_PAGE_SIZE);
  const [isListingLoading, setIsListingLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [appliedFilterValues, setAppliedFilterValues] = useState({ ...EMPTY_REKYV_LISTING_FILTERS });
  const [listingFilterFields, setListingFilterFields] = useState([]);
  const [listingSearchFields, setListingSearchFields] = useState({});
  const [showRcValidateConfirm, setShowRcValidateConfirm] = useState(false);
  const [rcValidateResponse, setRcValidateResponse] = useState(null);

  const uploadConfig = UPLOAD_CONFIG[requestType] || UPLOAD_CONFIG[FASTAG_BULK_REQUEST_TYPES.KYV];
  const isCsvUpload =
    requestType === FASTAG_BULK_REQUEST_TYPES.VRN_DETAIL ||
    requestType === FASTAG_BULK_REQUEST_TYPES.CHASSIS_TO_VRN ||
    requestType === FASTAG_BULK_REQUEST_TYPES.RC_UPLOAD;

  useEffect(() => {
    requestTypeRef.current = requestType;
  }, [requestType]);

  const loadListing = useCallback(async (page = 1, filters = appliedFilterValues) => {
    setIsListingLoading(true);
    try {
      const data = await fetchFastagBulkUploadListing({
        page,
        limit: LISTING_PAGE_SIZE,
        filters,
      });
      const results = Array.isArray(data?.results) ? data.results : [];
      const limit = data?.limit || LISTING_PAGE_SIZE;
      const count = data?.count ?? 0;

      setListData(results);
      setListingTotalCount(count);
      setListingLimit(limit);
      setListingPageCount(count > 0 ? Math.ceil(count / limit) : 0);
      setListingCurrentPage(data?.page || page);
      setListingColumns(data?.columns || {});
      if (Array.isArray(data?.filter_fields)) {
        setListingFilterFields(data.filter_fields);
      }
      if (data?.search_fields) {
        setListingSearchFields(data.search_fields);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Could not load upload history.';
      toast.error(message);
      setListData([]);
      setListingPageCount(0);
    } finally {
      setIsListingLoading(false);
    }
  }, [appliedFilterValues]);

  useEffect(() => {
    loadListing(1, appliedFilterValues);
  }, []);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setCsvFileLink('');
    setFileUploadResetKey((key) => key + 1);
    setFileError('');
    setProgress(null);
    setUploadResult(null);
    setUploadError('');
    setShowRcValidateConfirm(false);
    setRcValidateResponse(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFileSelection = useCallback((file) => {
    setUploadResult(null);
    setUploadError('');
    setProgress(null);

    if (!file) {
      setSelectedFile(null);
      setFileError('');
      return;
    }

    const error = validateFastagBulkFile(file, FASTAG_BULK_REQUEST_TYPES.KYV);
    if (error) {
      setSelectedFile(null);
      setFileError(error);
      return;
    }

    setSelectedFile(file);
    setFileError('');
  }, []);

  const handleCsvFileChange = (fileLink) => {
    setUploadResult(null);
    setUploadError('');
    setProgress(null);
    setShowRcValidateConfirm(false);
    setRcValidateResponse(null);
    setCsvFileLink(fileLink || '');
  };

  const handleRequestTypeChange = (event) => {
    const nextRequestType = event.target.value;
    requestTypeRef.current = nextRequestType;
    setRequestType(nextRequestType);
    resetState();
  };

  const onInputChange = (event) => {
    handleFileSelection(event.target.files?.[0] || null);
  };

  const onDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    if (isUploading) return;
    const file = event.dataTransfer.files?.[0];
    handleFileSelection(file || null);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    if (!isUploading) setIsDragOver(true);
  };

  const onDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const submitBulkUpload = async (activeRequestType, fileLinkOrZipResult) => {
    const activeUploadConfig =
      UPLOAD_CONFIG[activeRequestType] || UPLOAD_CONFIG[FASTAG_BULK_REQUEST_TYPES.KYV];
    const isCsvRequest =
      activeRequestType === FASTAG_BULK_REQUEST_TYPES.VRN_DETAIL ||
      activeRequestType === FASTAG_BULK_REQUEST_TYPES.CHASSIS_TO_VRN ||
      activeRequestType === FASTAG_BULK_REQUEST_TYPES.RC_UPLOAD;

    setIsUploading(true);
    setUploadError('');
    setUploadResult(null);
    setProgress({ phase: isCsvRequest ? 'submitting' : 'creating', percent: 0 });

    try {
      if (isCsvRequest) {
        setProgress({ phase: 'submitting', percent: 60 });
        await submitFastagBulkUpload(fileLinkOrZipResult, activeRequestType);
        setProgress({ phase: 'done', percent: 100 });
        setUploadResult({ file_link: fileLinkOrZipResult });
      } else {
        const result = await uploadFastagZipFile(fileLinkOrZipResult, setProgress);
        setProgress({ phase: 'submitting', percent: 95 });
        await submitFastagBulkUpload(result.cdn_url, activeRequestType);
        setProgress({ phase: 'done', percent: 100 });
        setUploadResult(result);
      }

      toast.success(activeUploadConfig.toastSuccess);
      await loadListing(1, appliedFilterValues);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Upload failed. Please try again.';
      setUploadError(message);
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpload = async () => {
    const activeRequestType = requestTypeRef.current;
    const isCsvRequest =
      activeRequestType === FASTAG_BULK_REQUEST_TYPES.VRN_DETAIL ||
      activeRequestType === FASTAG_BULK_REQUEST_TYPES.CHASSIS_TO_VRN ||
      activeRequestType === FASTAG_BULK_REQUEST_TYPES.RC_UPLOAD;
    const isRcUpload = activeRequestType === FASTAG_BULK_REQUEST_TYPES.RC_UPLOAD;

    if (isUploading) return;
    if (isCsvRequest && !csvFileLink) return;
    if (!isCsvRequest && !selectedFile) return;

    // RC_UPLOAD: validate first, then confirm before submit.
    if (isRcUpload) {
      setIsUploading(true);
      setUploadError('');
      setUploadResult(null);
      setProgress({ phase: 'validating', percent: 40 });

      try {
        const validationBody = await validateFastagBulkUploadRequest(
          csvFileLink,
          'validate_rc_image_prices'
        );
        setRcValidateResponse(validationBody);
        setShowRcValidateConfirm(true);
        setProgress(null);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Validation failed. Please try again.';
        setUploadError(message);
        toast.error(message);
        setProgress(null);
      } finally {
        setIsUploading(false);
      }
      return;
    }

    if (isCsvRequest) {
      await submitBulkUpload(activeRequestType, csvFileLink);
      return;
    }

    await submitBulkUpload(activeRequestType, selectedFile);
  };

  const handleRcValidateConfirmNo = () => {
    setShowRcValidateConfirm(false);
    setRcValidateResponse(null);
    setProgress(null);
  };

  const handleRcValidateConfirmYes = async () => {
    setShowRcValidateConfirm(false);
    const fileLink = csvFileLink;
    setRcValidateResponse(null);
    if (!fileLink) return;
    await submitBulkUpload(FASTAG_BULK_REQUEST_TYPES.RC_UPLOAD, fileLink);
  };

  const rcValidateDisplayText = useMemo(() => {
    if (!rcValidateResponse) return '';
    const dataMessage = rcValidateResponse?.data?.message;
    if (typeof dataMessage === 'string' && dataMessage) {
      return dataMessage;
    }
    if (typeof rcValidateResponse.message === 'string' && rcValidateResponse.message) {
      return rcValidateResponse.message;
    }
    return '';
  }, [rcValidateResponse]);

  const onListingPageChange = async (val) => {
    await loadListing(val.selected + 1, appliedFilterValues);
  };

  const filterToggle = () => {
    setShowFilter(!showFilter);
  };

  const applyFilter = async (filterData) => {
    setAppliedFilterValues(filterData);
    await loadListing(1, filterData);
    if (showFilter) {
      setShowFilter(false);
    }
  };

  const removeFilters = async () => {
    const cleared = { ...EMPTY_REKYV_LISTING_FILTERS };
    setAppliedFilterValues(cleared);
    await loadListing(1, cleared);
  };

  const appliedFilterLabels = useMemo(() => {
    const labels = [];
    if (appliedFilterValues.request_status) {
      labels.push(`File Status: ${appliedFilterValues.request_status}`);
    }
    const from = appliedFilterValues.created_date_from;
    const to = appliedFilterValues.created_date_to;
    const fromValid = from && String(from).toLowerCase() !== 'invalid date';
    const toValid = to && String(to).toLowerCase() !== 'invalid date';
    if (fromValid || toValid) {
      labels.push(`Created: ${fromValid ? from : '—'} to ${toValid ? to : '—'}`);
    }
    if (appliedFilterValues.request_id) {
      labels.push(`Request ID: ${appliedFilterValues.request_id}`);
    }
    if (appliedFilterValues.requestor_name) {
      labels.push(`Requestor: ${appliedFilterValues.requestor_name}`);
    }
    return labels;
  }, [appliedFilterValues]);

  const tableColumns = useMemo(
    () => buildTableColumns(listingColumns),
    [listingColumns]
  );

  const listingSrNo =
    listingCurrentPage > 0
      ? (listingCurrentPage - 1) * listingLimit + 1
      : 1;

  const progressLabel = progress
    ? PHASE_LABELS[progress.phase] || 'Uploading…'
    : '';

  return (
    <Layout title="FASTag Compliance" Buttons={() => null} pagination={false}>
      <div className="fastag-rekyv-page mt-2">
        <div className="fastag-rekyv-page-inner">
            <div className="fastag-rekyv-card fastag-rekyv-upload-card">
              <div className="fastag-rekyv-section-header">
                <div>
                  <h5 className="mb-1">{uploadConfig.title}</h5>
                  <p className="text-muted mb-0 fastag-rekyv-section-subtitle">
                    {uploadConfig.subtitle}
                  </p>
                </div>
              </div>

              <div className="fastag-rekyv-request-type mt-4">
                <label htmlFor="fastag-rekyv-request-type" className="fastag-rekyv-request-type-label">
                  Request Type
                </label>
                <select
                  id="fastag-rekyv-request-type"
                  className="form-control fastag-rekyv-request-type-select"
                  value={requestType}
                  onChange={handleRequestTypeChange}
                  disabled={isUploading}
                >
                  {REQUEST_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {!uploadResult && (
                <>
                  {isCsvUpload ? (
                    <div className="fastag-rekyv-csv-upload mt-4">
                      <FileUpload
                        key={fileUploadResetKey}
                        id={`fastag-${requestType.toLowerCase()}-csv`}
                        accept=".csv,text/csv"
                        documentUploadUrl={documentUploadUrlPublic}
                        documentUrlData={documentUrlDataPublic}
                        onChange={handleCsvFileChange}
                      >
                        <span className="btn btn-primary">Choose CSV file</span>
                      </FileUpload>
                      <p className="text-muted mb-0 mt-3 fastag-rekyv-section-subtitle">
                        The CSV is uploaded immediately. Click submit after the file is attached.
                      </p>
                    </div>
                  ) : (
                    <div
                      className={`fastag-rekyv-dropzone mt-4 ${
                        isDragOver ? 'is-dragover' : ''
                      } ${isUploading ? 'is-disabled' : ''}`}
                      onClick={() => !isUploading && fileInputRef.current?.click()}
                      onDrop={onDrop}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          if (!isUploading) fileInputRef.current?.click();
                        }
                      }}
                    >
                      <div className="fastag-rekyv-dropzone-icon" aria-hidden>
                        {uploadConfig.dropzoneIcon}
                      </div>
                      <div className="font-weight-bold">{uploadConfig.dropzoneLabel}</div>
                      <div className="text-muted mt-1 fastag-rekyv-section-subtitle">
                        or click to browse
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={uploadConfig.accept}
                        style={{ display: 'none' }}
                        onChange={onInputChange}
                        disabled={isUploading}
                      />
                    </div>
                  )}

                  <div className="fastag-rekyv-constraints">
                    <div>Allowed format: <strong>{uploadConfig.formatLabel}</strong> only</div>
                    {isCsvUpload ? (
                      <div>Maximum size: <strong>{uploadConfig.maxSizeLabel}</strong></div>
                    ) : (
                      <>
                        <div>Maximum size: <strong>{uploadConfig.maxSizeLabel}</strong></div>
                        <div>Content-Type: <strong>{uploadConfig.contentTypeLabel}</strong></div>
                      </>
                    )}
                  </div>

                  <div className="fastag-rekyv-sample-download">
                    <span className="text-muted fastag-rekyv-section-subtitle">
                      Need a reference format?
                    </span>
                    <button
                      type="button"
                      className="fastag-rekyv-sample-link"
                      onClick={async () => {
                        try {
                          await uploadConfig.downloadSample();
                        } catch (error) {
                          toast.error(
                            error?.message || 'Could not download sample file.'
                          );
                        }
                      }}
                      disabled={isUploading}
                    >
                      Download sample file
                    </button>
                  </div>

                  {fileError && (
                    <p className="text-danger mt-3 mb-0">{fileError}</p>
                  )}

                  {!isCsvUpload && selectedFile && !fileError && (
                    <div className="fastag-rekyv-file-card">
                      <div className="fastag-rekyv-file-icon" aria-hidden>
                        {uploadConfig.fileIcon}
                      </div>
                      <div className="fastag-rekyv-file-meta">
                        <div className="fastag-rekyv-file-name">{selectedFile.name}</div>
                        <div className="fastag-rekyv-file-details">
                          Size: {formatFileSize(selectedFile.size)}
                          {' · '}
                          Limit: {formatFileSize(uploadConfig.maxBytes)}
                        </div>
                      </div>
                      {!isUploading && (
                        <Button
                          variant="link"
                          className="text-danger p-0"
                          onClick={(event) => {
                            event.stopPropagation();
                            resetState();
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  )}

                  {progress && isUploading && (
                    <div className="fastag-rekyv-progress">
                      <p className="fastag-rekyv-progress-message mb-2">
                        This may take a few minutes. Please keep this page open until the upload
                        finishes.
                      </p>
                      <div className="fastag-rekyv-progress-label">
                        <span>{progressLabel}</span>
                        <span>{progress.percent}%</span>
                      </div>
                      <div className="fastag-rekyv-progress-bar">
                        <div
                          className="fastag-rekyv-progress-fill"
                          style={{ width: `${progress.percent}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {uploadError && (
                    <p className="text-danger mt-3 mb-0">{uploadError}</p>
                  )}

                  <div className="fastag-rekyv-actions">
                    <Button
                      variant="primary"
                      disabled={
                        isUploading ||
                        !!fileError ||
                        (isCsvUpload ? !csvFileLink : !selectedFile)
                      }
                      onClick={handleUpload}
                    >
                      {isUploading ? 'Uploading…' : uploadConfig.uploadButtonLabel}
                    </Button>
                    {(isCsvUpload ? csvFileLink : selectedFile) && !isUploading && (
                      <Button variant="outline-secondary" onClick={resetState}>
                        Clear
                      </Button>
                    )}
                  </div>
                </>
              )}

              {uploadResult && (
                <div className="fastag-rekyv-success mt-4">
                  <img
                    src={Images?.tick}
                    alt=""
                    className="fastag-rekyv-success-icon"
                  />
                  <h5 className="mb-1">{uploadConfig.successTitle}</h5>
                  <p className="text-muted mb-0 fastag-rekyv-section-subtitle">
                    {uploadConfig.successSubtitle}
                  </p>

                  <div className="fastag-rekyv-actions justify-content-center">
                    <Button variant="primary" onClick={resetState}>
                      Upload another file
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="fastag-rekyv-card fastag-rekyv-listing-card">
              <div className="fastag-rekyv-section-header fastag-rekyv-listing-header">
                <div>
                  <h5 className="mb-1">Upload History</h5>
                  <p className="text-muted mb-0 fastag-rekyv-section-subtitle">
                    Track FASTag bulk upload requests and processing status.
                  </p>
                </div>
                <div className="fastag-rekyv-listing-meta">
                  <span className="fastag-rekyv-listing-count">
                    {listingTotalCount} total request{listingTotalCount === 1 ? '' : 's'}
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={filterToggle}
                    className="fastag-rekyv-filter-btn"
                  >
                    Filters
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={isListingLoading}
                    onClick={() => loadListing(listingCurrentPage, appliedFilterValues)}
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              <AppliedFiltersBar
                items={appliedFilterLabels}
                onClear={removeFilters}
                clearButtonLabel="Remove filters"
                clearButtonDisabled={isListingLoading}
                className="fastag-rekyv-applied-filters"
              />

              <TableSectionV2
                hideHeader
                hideTitleAndStatusFilter
                className="fastag-rekyv-table-section"
                tableData={listData}
                tableColumns={tableColumns}
                tableSectionProps={{ srNo: listingSrNo }}
                pageCount={listingPageCount}
                currentPage={listingCurrentPage}
                onPageChange={onListingPageChange}
                isLoading={isListingLoading}
                emptyMessage="No bulk uploads found."
              />
            </div>
        </div>
      </div>

      <FastagReKyvListingFilter
        showFilter={showFilter}
        filterToggle={filterToggle}
        applyFilter={applyFilter}
        filterFields={listingFilterFields}
        searchFields={listingSearchFields}
        appliedFilterValues={appliedFilterValues}
      />

      <ModalPop
        title=""
        show={showRcValidateConfirm}
        handleModalHide={handleRcValidateConfirmNo}
        centered
      >
        <div className="text-center">
          <div className="mb-3">
            <img
              src={Images?.ConfirmationIcon}
              alt=""
              style={{
                width: '72px',
                maxWidth: '20%',
                height: 'auto',
                display: 'inline-block',
              }}
            />
          </div>
          {rcValidateDisplayText ? (
            <p
              className="mb-4"
              style={{
                wordBreak: 'break-word',
              }}
            >
              {rcValidateDisplayText}
            </p>
          ) : null}
          <div className="d-flex justify-content-center">
            <Button
              className="btn btn-danger mx-2 px-4"
              onClick={handleRcValidateConfirmNo}
              disabled={isUploading}
            >
              No
            </Button>
            <Button
              className="btn btn-success mx-2 px-4"
              onClick={handleRcValidateConfirmYes}
              disabled={isUploading}
            >
              Yes
            </Button>
          </div>
        </div>
      </ModalPop>
    </Layout>
  );
};

export default FastagReKyV;
